// @flow
import React, {useContext, useEffect, useState} from "react"
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import Header from "./Header";
import BackButton from "../GUI_COMMON/COMPONENTS/BackButton";
import ArchiverClientApi from "../API/archiverClientApi";
import {useHistory, useLocation} from "react-router-dom";
import BackNextWidget from "../GUI_COMMON/COMPONENTS/BackNextWidget";
import SampleSearchInput from "./SampleSearchInput";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import {saveSearchResults} from "../ACTIONS/clientActions";
import BoxCard from "./BoxCard";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";

const DEFAULT_BOX_PAGE_SIZE=20;

const SearchBoxesPage = () => {
    
    const [globalState, dispatch] = useContext(StoreContext);
    const [isSearching,setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const [term,setTerm] = useState(params.get('s')??"");
    const [isIniting, setIsIniting] = useState(true);
    const [searchPressed,setSearchPressed] = useState(false);
    
    const [pageSize,] = useState(DEFAULT_BOX_PAGE_SIZE);
    const [startIndex, setStartIndex]=useState(Number(params.get('si')??0));
    
    useEffect(() => {
        // RECALL -- this runs AFTER all children mount!
        console.debug("SearchBoxesPage starting up...");
        window.scrollTo(0,0);
        LIUtils.setBackgroundColor('--li-main-bg-color');

        setIsIniting(false);
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        if(!isIniting) {
            setSearchPressed(false); // reset
            if(searchResultsHaveNotChanged()) {
                setSearchResults(globalState.searchResults.searchResults);
            }
            else { // go to the API
                handleBackNext(startIndex, pageSize);
            }
        }
    }, [term, isIniting, searchPressed]); // eslint-disable-line react-hooks/exhaustive-deps

    function buildSearchTypeKey():string {
        return "box_search";
    }
    
    function searchResultsHaveNotChanged() {
        const searchTypeKey = buildSearchTypeKey();
        const gSearchResults = globalState.searchResults;

        return gSearchResults.searchType === searchTypeKey &&
            gSearchResults.startIndex === startIndex &&
            gSearchResults.numToReturn === pageSize &&
            gSearchResults.searchResults !== undefined &&
            gSearchResults.searchTerm === term && 
            !searchPressed;
    }
    
    function handleBoxSearchClicked(newTerm) {
        setStartIndex(0);
        setSearchPressed(true);
        if(newTerm===term) {
            handleBackNext(startIndex, pageSize); // force refresh if same term (useEffect won't fire)
        }
        setTerm(newTerm);
    }

    function handleBoxClicked(boxItem) {
        replaceBrowserHistory(term,startIndex,DEFAULT_BOX_PAGE_SIZE);
        history.push(`/box_history/${boxItem.barcode}`);
    }
    
    function handleBackNext(si, numToReturn) {

        console.debug(`*** handling backnext (${si}:${numToReturn}) ***`);
        setStartIndex(si);
        replaceBrowserHistory(term,si,numToReturn);
        if(!term || term==="") {
            return null;
        }
        setIsSearching(true);
        setSearchResults([]);
        ArchiverClientApi.sendBoxSearchRequest(term,si, numToReturn)
        .then( (results) => {
            setSearchResults(results);
            dispatch(saveSearchResults(buildSearchTypeKey(),results,si, numToReturn, term)); // save for possible later use
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => setIsSearching(false));
    }

    function replaceBrowserHistory(searchTerm,startIndex,numToReturn) {
        history.replace(`/box_search?s=${searchTerm}&si=${startIndex}&nr=${numToReturn}`);
    }
    
    function showResultsTable() {
        if(!term || term==="") {
            return <div className="text-center mt-5"/>;
        }
        if(isSearching) {
            return <div className="text-center mt-5">...searching...</div>;
        }
        if(!searchResults || searchResults.length===0) {
            return <div className="badge-pill mt-5 p-3 li-bg-card">no matches found</div>;
        }
        if(searchResults.length%2!==0) { // need an extra one to keep the last left sample to the left (hack)
            searchResults.push(null); // final one
        }

        const resultList = searchResults.map( (boxItem, index) => {
            
                return <BoxCard key={index} info={boxItem} onClick={() => handleBoxClicked(boxItem)}/>;
            }
        );

        return (
            <div className="row justify-content-center">
                {resultList}
            </div>
        );
    }

    function haveResults() {
        return !isSearching && ((searchResults && searchResults.length>0) || startIndex>0);
    }
    
    return (
        <div className="container">
            <Header/>
            <div className="row li-header-row m-0 p-0 justify-content-between">
                <div className="align-self-center">
                    <BackButton onClick={() => history.push("/dashboard")}/>
                    <h1 className="d-inline-block align-middle">Search Boxes</h1>
                </div>
            </div>
                
            <div className="row pt-4 pb-3 pl-0 justify-content-center align-items-center align-self-center">
                <SampleSearchInput name="boxSearch" label="Box Barcode:" onSearchClicked={handleBoxSearchClicked} 
                                   busyFlag={isSearching} 
                                   initialValue={term} medium placeholder="" hasFocus/>
            </div>

            <div className="mt-3 mb-4">
                <BackNextWidget disableNext={searchResults.length < pageSize} pageSize={pageSize} startIndex={startIndex} 
                                onClick={handleBackNext} show={haveResults()}/>
                <div className="li-search-results-table mt-2">
                    {showResultsTable()}
                </div>
            </div>
            
            <BackNextWidget disableNext={searchResults.length < pageSize} pageSize={pageSize} startIndex={startIndex} 
                            onClick={handleBackNext} show={haveResults()}/>
        </div>
    );
}

export default SearchBoxesPage;